import { notifyManager, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useAuth } from "@/lib/auth";
import { recentDialedLead } from "@/lib/dialer";
import {
  pageLead, sameWorkLead, windowSession, workLead, type WorkLead,
} from "@/lib/phone-work";
import { useDialedLead, useKeys, useMyAcdSessions } from "@/lib/queries";
import { accountScope } from "@/lib/session-cache";
import type { DialerCall } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { useAcdCall } from "@/softphone/acd-call";
import { useRepAvailability } from "@/softphone/availability";
import { useDialerWork } from "@/softphone/dialer-offer";
import { useManualCallWork } from "@/softphone/manual-call";
import type { Call } from "@/softphone/softphone-context";

/**
 * The lead the Phone page works with (`lib/phone-work.ts` holds the rules): whose card, script and
 * form are beside the phone, and the one the page keeps once the call and its outcome window are
 * over, so a rep who has not finished the form still finds it.
 *
 * The kept lead is module state, like the page's stage (`stage.ts`) and the planned outcome
 * (`planned-outcome.ts`): the page unmounts when the rep opens a contact and comes back, and the
 * lead must still be there. It belongs to one account (`accountScope`, the same key the form's
 * answers are held under) and is forgotten when another signs in.
 *
 * Beside the lead the store remembers the phone's call they were live on. The rep's record can let
 * go of a call a moment before the phone hears its leg end, and for that moment only this id says
 * the call still at the phone is theirs, and not a stranger's that must clear the page.
 */

let owner: string | null = null;
let kept: WorkLead | null = null;
/** The phone call the kept lead was live on, or null when they have not been on one. */
let keptCall: string | null = null;
const watchers = new Set<() => void>();

function watch(notify: () => void): () => void {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
}

/** Keep this lead for this account, with the call they were live on. Another account's lead is
 *  dropped first, whatever is kept. */
function keep(account: string, lead: WorkLead | null, callId: string | null): void {
  if (owner === account && sameWorkLead(kept, lead) && keptCall === callId) return;
  owner = account;
  kept = lead;
  keptCall = callId;
  for (const notify of watchers) notify();
}

/** Forget the kept lead and whose it was. For tests, which share the module between cases. */
export function resetWorkLead(): void {
  owner = null;
  kept = null;
  keptCall = null;
  for (const notify of watchers) notify();
}

/**
 * The lead the dialer is calling, as `Calling` in `dialer-offer.tsx` reads it, without asking for
 * it a second time. Read from the cache and not through `useDialerCall`: that query is dropped the
 * moment nothing shows it (`gcTime: 0`) so the next dial never opens on the last one's lead, and a
 * second reader that is on the page all day would hold the last dial in the cache for good.
 */
function useDialerCallSeen(on: boolean): DialerCall | null {
  const qc = useQueryClient();
  const key = useKeys().dialerCall();
  const subscribe = React.useCallback(
    // Batched as the library's own cache readers are: the cache also speaks while another
    // component renders, and a store must not.
    (notify: () => void) => qc.getQueryCache().subscribe(notifyManager.batchCalls(notify)),
    [qc],
  );
  const read = () => (on ? (qc.getQueryData<DialerCall | null>(key) ?? null) : null);
  return React.useSyncExternalStore(subscribe, read, read);
}

/**
 * The lead on the page for the phone's `active` call (the Phone page's own choice of call, or null
 * between calls), whether something live still names them, and how the rep puts a kept lead away.
 *
 * Nothing is asked of the API that the page does not ask already: the rep's record and the
 * dialer's offer are the dock's, the session list is read only while the outcome window is open
 * (where `DispositionView` reads the same list), and the dialer's call is read from the cache. In
 * the gallery at `/dev/phone` nobody is signed in and the seeded cache is what all of them read,
 * as `useRepAvailability` says.
 */
export function useWorkLead(
  active: Call | null,
  /** The active call is ringing in and the phone is picking it up by itself: to the page it is
   *  a call the rep is on, not a ring that has the page to itself. */
  pickedUp = false,
): {
  lead: WorkLead | null;
  /** A call, a dial or the outcome window still names the lead; false once it is only kept. */
  live: boolean;
  /** Put a kept lead away. Does nothing while the lead is live. */
  close: () => void;
  /** The active call is the ACD's call: a campaign call, with or without a lead. */
  campaignCall: boolean;
} {
  const { user } = useAuth();
  const account = accountScope(user);
  const acd = useAcdCall();
  const current = useRepAvailability();
  const manual = useManualCallWork();
  const work = useDialerWork();
  const dialerCall = useDialerCallSeen(work.dialing);
  const dialedLead = useDialedLead();
  const now = useNow(work.dialing ? 10_000 : null);
  const stored = React.useSyncExternalStore(watch, () => kept, () => kept);
  const storedCall = React.useSyncExternalStore(watch, () => keptCall, () => keptCall);
  // Another account's lead is not this one's, even for the one render before the effect drops it.
  const mine = owner === account ? stored : null;
  const mineCall = owner === account ? storedCall : null;

  // Only while the window is open, and by the same key as the window's own reading.
  const inWindow = current?.view.state === "disposition";
  const agent = current ? { user: current.me.user, domain: current.me.domain } : null;
  const sessions = useMyAcdSessions(inWindow ? agent : null);
  const session = inWindow ? windowSession(sessions.data ?? [], agent) : null;
  // A list still being read again may be the last window's, whose newest session is the call
  // before this one. Until it has been read, it is believed only for the lead already on the page.
  const settled = session !== null && (!sessions.isFetching || session.lead_id === mine?.id);

  const dialed = work.dialing ? (dialerCall ?? recentDialedLead(dialedLead, now)) : null;
  const live = workLead({
    call:
      acd !== null && acd.callId !== null
        ? {
            leadId: acd.leadId,
            stay: acd.sessionId ?? acd.callerChannelId,
            campaignId: acd.line?.campaign_id ?? null,
          }
        : null,
    window:
      session !== null && settled
        ? { leadId: session.lead_id, stay: session.session_id, campaignId: session.campaign_id }
        : null,
    // The manual call's record outlives its call, so it names a lead only while it is shown.
    manual:
      manual.shown && manual.call
        ? { leadId: manual.call.lead.id, campaignId: manual.call.campaign_id }
        : null,
    dial: dialed ? { leadId: dialed.lead.id, campaignId: dialed.campaign_id } : null,
    offer: work.offer
      ? { leadId: work.offer.lead.id, campaignId: work.offer.campaign_id }
      : null,
  });

  const ringing =
    active !== null && active.direction === "inbound" && active.state === "ringing" && !pickedUp;
  const shown = pageLead({
    kept: mine,
    live,
    ringing,
    onCall: active !== null && !ringing,
    dialing: work.dialing,
    sameCall: active !== null && mineCall !== null && mineCall === active.id,
  });

  const next = shown.kept;
  // The call the kept lead is live on, while a call names them: the ACD's own call, or the rep's
  // manual dial once it is at the phone. It is remembered for as long as the same lead is kept,
  // so it is still known when the record has let go of the call and the leg is not over yet.
  const nextCall =
    next === null
      ? null
      : shown.live && next.from === "call" && acd !== null && acd.callId !== null
        ? acd.callId
        : shown.live && next.from === "manual" && active !== null
          ? active.id
          : next.id === mine?.id
            ? mineCall
            : null;
  React.useEffect(() => {
    keep(account, next, nextCall);
    // `next` is a new object at every render: the store compares it field by field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, next?.id, next?.stay, next?.campaignId, next?.from, nextCall]);

  const isLive = shown.live;
  const close = React.useCallback(() => {
    if (!isLive) keep(account, null, null);
  }, [account, isLive]);

  // One object per lead, so a pane keyed or memoised on it does not see a new lead at every render.
  const { lead } = shown;
  const steady = React.useMemo(
    () => lead,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lead?.id, lead?.stay, lead?.campaignId, lead?.from],
  );

  return {
    lead: steady,
    live: shown.live,
    close,
    campaignCall: acd !== null && active !== null && acd.callId === active.id,
  };
}
